import React from 'react';

export type DiffType = 'equal' | 'insert' | 'delete';

export interface DiffSegment {
  type: DiffType;
  text: string;
}

/**
 * Calculate the differences between two texts using a simple diff algorithm
 * Based on the Myers diff algorithm concept
 */
export function calculateDiff(oldText: string = '', newText: string = ''): DiffSegment[] {
  if (!oldText && !newText) return [];
  if (!oldText) return [{ type: 'insert', text: newText }];
  if (!newText) return [{ type: 'delete', text: oldText }];

  const oldWords = oldText.split(/(\s+)/);
  const newWords = newText.split(/(\s+)/);
  
  const dp: number[][] = [];
  for (let i = 0; i <= oldWords.length; i++) {
    dp[i] = [];
    for (let j = 0; j <= newWords.length; j++) {
      if (i === 0) {
        dp[i][j] = j;
      } else if (j === 0) {
        dp[i][j] = i;
      } else if (oldWords[i - 1] === newWords[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
      }
    }
  }

  const diff: DiffSegment[] = [];
  let i = oldWords.length;
  let j = newWords.length;

  while (i > 0 || j > 0) {
    if (i === 0) {
      diff.unshift({ type: 'insert', text: newWords[j - 1] });
      j--;
    } else if (j === 0) {
      diff.unshift({ type: 'delete', text: oldWords[i - 1] });
      i--;
    } else if (oldWords[i - 1] === newWords[j - 1]) {
      diff.unshift({ type: 'equal', text: oldWords[i - 1] });
      i--;
      j--;
    } else {
      const delCost = dp[i - 1][j];
      const insCost = dp[i][j - 1];
      const subCost = dp[i - 1][j - 1];

      if (subCost <= delCost && subCost <= insCost) {
        diff.unshift({ type: 'delete', text: oldWords[i - 1] });
        diff.unshift({ type: 'insert', text: newWords[j - 1] });
        i--;
        j--;
      } else if (delCost <= insCost) {
        diff.unshift({ type: 'delete', text: oldWords[i - 1] });
        i--;
      } else {
        diff.unshift({ type: 'insert', text: newWords[j - 1] });
        j--;
      }
    }
  }

  // Merge consecutive segments of the same type
  const mergedDiff: DiffSegment[] = [];
  for (const segment of diff) {
    if (mergedDiff.length > 0 && mergedDiff[mergedDiff.length - 1].type === segment.type) {
      mergedDiff[mergedDiff.length - 1].text += segment.text;
    } else {
      mergedDiff.push(segment);
    }
  }

  return mergedDiff;
}

interface DiffRendererProps {
  diff: DiffSegment[];
}

/**
 * Component to render diff results with color coding
 */
export const DiffRenderer: React.FC<DiffRendererProps> = ({ diff }) => {
  return (
    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {diff.map((segment, index) => {
        switch (segment.type) {
          case 'insert':
            return (
              <span
                key={index}
                style={{
                  backgroundColor: '#e6f7e6',
                  color: '#000',
                }}
              >
                {segment.text}
              </span>
            );
          case 'delete':
            return (
              <span
                key={index}
                style={{
                  backgroundColor: '#ffe6e6',
                  color: '#000',
                  textDecoration: 'line-through',
                }}
              >
                {segment.text}
              </span>
            );
          case 'equal':
          default:
            return <span key={index}>{segment.text}</span>;
        }
      })}
    </div>
  );
};

